import { useCallback } from 'react';
import { getIn } from 'formik';
import { Grid } from '@mui/material';

import { disabledFutureDate } from '../../../../helpers';
import {
  FormAutocompleteField,
  FormCheckboxField,
  FormDateField,
  FormNumberField,
  FormPhoneField,
  FormTextField,
} from '../../components';
import {
  THERAPY_FREQUENCY_OPTIONS,
  THERAPY_PROGRESS_OPTIONS,
  THERAPY_RECORD_SHARED_OPTIONS,
  THERAPY_SERVICE_TYPE_OPTIONS,
} from '../constants';

import type { TherapyFieldsProps } from './types';

export default function TherapyFields({
  formik,
  disabled,
  readOnly,
  saveField,
  backgroundColor,
  monthFormat,
  includeRecordShared = false,
}: TherapyFieldsProps) {
  const handleDoNotShareChange = useCallback(
    (name: 'do_not_share_info_with_provider', checked: boolean) => {
      saveField(name, checked);
      if (includeRecordShared && checked) {
        void formik.setFieldValue('is_record_shared', '');
        void formik.setFieldTouched('is_record_shared', false);
      }
    },
    [formik, includeRecordShared, saveField],
  );

  return (
    <Grid container spacing={2} my={1}>
      <Grid item xs={12} sm={6} md={3.5}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          readOnly={readOnly}
          label="Provider name"
          name="provider_name"
          onBlur={saveField}
          placeholder="Enter provider name"
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.5}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          readOnly={readOnly}
          label="Service type"
          name="service_type"
          onChange={saveField}
          options={THERAPY_SERVICE_TYPE_OPTIONS}
          placeholder="Select service type"
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2.5}>
        <FormPhoneField
          disabled={disabled}
          formik={formik}
          readOnly={readOnly}
          label="Phone number"
          name="phone_number"
          onBlur={saveField}
          placeholder="Enter phone number"
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3.5}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          readOnly={readOnly}
          label="Email"
          name="email"
          onBlur={saveField}
          placeholder="Enter email address"
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormDateField
          backgroundColor={backgroundColor}
          disabled={disabled}
          disabledDate={disabledFutureDate}
          formik={formik}
          format={monthFormat}
          readOnly={readOnly}
          label="Start month"
          name="start_date"
          onChange={saveField}
          picker="month"
          placeholder={monthFormat}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormDateField
          backgroundColor={backgroundColor}
          disabled={disabled}
          disabledDate={disabledFutureDate}
          formik={formik}
          format={monthFormat}
          readOnly={readOnly}
          label="End month"
          name="end_date"
          onChange={saveField}
          picker="month"
          placeholder={monthFormat}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormNumberField
          disabled={disabled}
          formik={formik}
          readOnly={readOnly}
          label="Hours"
          min={0}
          name="hours"
          onBlur={saveField}
          placeholder="Enter hours"
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          readOnly={readOnly}
          label="Service frequency"
          name="frequency"
          onChange={saveField}
          options={THERAPY_FREQUENCY_OPTIONS}
          placeholder="Select frequency"
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          readOnly={readOnly}
          label="Progress"
          name="progress"
          onChange={saveField}
          options={THERAPY_PROGRESS_OPTIONS}
          placeholder="Select progress"
          required
        />
      </Grid>
      <Grid item xs={12}>
        <FormCheckboxField
          disabled={disabled}
          formik={formik}
          readOnly={readOnly}
          label="As the guardian, I do NOT provide authorization to release information to this provider."
          name="do_not_share_info_with_provider"
          onChange={handleDoNotShareChange}
        />
      </Grid>
      {includeRecordShared && !getIn(formik.values, 'do_not_share_info_with_provider') && (
        <Grid item xs={12} sm={6} md={3}>
          <FormAutocompleteField
            disabled={disabled}
            formik={formik}
            readOnly={readOnly}
            label="Record shared"
            name="is_record_shared"
            onChange={saveField}
            options={THERAPY_RECORD_SHARED_OPTIONS}
            placeholder="Select record shared"
            required
          />
        </Grid>
      )}
    </Grid>
  );
}
